/**
 * Wiki reference:
 * https://gitlab.com/stefano.g/pathfinder_dev/-/wikis/Frontend/Context/CostModelsContext
 */

import React, { useCallback, useContext, useEffect, useRef } from "react";

// Third-party
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/browser";

// Services
import { getScenarioConfigs } from "services/scenario";
import { getCostModelParameters } from "services/models";
import { getAvailableCostModels } from "services/cost_models";
import { getSitingScenarioConfigs } from "services/siting";

const DispatchContext = React.createContext();
const StateContext = React.createContext();

const initialState = {
  scenarioCostModels: null, // Cost models available in the current scenario.
  scenarioCostModelsParameters: null, // Parameters for the cost models of the current scenario.
  costModelsInfo: null, // Information of all the cost models available in the company.
  isFetching: false, // If true, the hook is fetching data.
  error: null // Last reported error.
};

function costModelsReducer(state, action) {
  switch (action.type) {
    // Action to set the available cost models for the scenario,
    // the parameters for each cost model in the scenario and the
    // information of all the cost models.
    case "COST_MODELS_DATA_SET": {
      return {
        ...state,
        isFetching: false,
        scenarioCostModels: action.payload.scenarioCostModels,
        scenarioCostModelsParameters:
          action.payload.scenarioCostModelsParameters,
        costModelsInfo: action.payload.costModelsInfo
      };
    }

    // Action to update only the cost models info (e.g., after a language
    // change) without touching the scenario-specific data.
    case "COST_MODELS_INFO_SET": {
      return {
        ...state,
        costModelsInfo: action.payload.costModelsInfo
      };
    }

    // Action to set the isFetching status to true.
    case "FETCHING_START": {
      return {
        ...state,
        isFetching: true,
        error: null
      };
    }

    // Action to report an error.
    case "FETCHING_ERROR": {
      return {
        ...state,
        isFetching: false,
        error: action.payload.error
      };
    }

    // Action to report a recoverable error without ending the active fetch.
    case "ERROR_SET": {
      return {
        ...state,
        error: action.payload.error
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Provides all children with the state and the dispatch of Cost Models Provider
 * @param { Object } props - Properties of the component
 * @param { ReactNode } props.children - Children components
 * @returns node
 */
function CostModelsProvider({ children }) {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [state, dispatch] = React.useReducer(costModelsReducer, initialState);

  const prevLanguageRef = useRef(i18n.language);

  // Refetch the (localized) cost models info when the UI language changes,
  // but only if it was already fetched once.
  useEffect(() => {
    if (prevLanguageRef.current === i18n.language) return;
    prevLanguageRef.current = i18n.language;

    if (!state.costModelsInfo) return;

    let cancelled = false;
    async function refetchCostModelsInfo() {
      try {
        const costModelsInfo = await getAvailableCostModels();
        if (cancelled) return;
        dispatch({
          type: "COST_MODELS_INFO_SET",
          payload: { costModelsInfo }
        });
      } catch (error) {
        if (cancelled) return;
        dispatch({ type: "FETCHING_ERROR", payload: { error } });
        enqueueSnackbar(t("CostModelConfig.ErrorGettingModels"), {
          variant: "error"
        });
        Sentry.captureException(error);
      }
    }

    refetchCostModelsInfo();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, state.costModelsInfo, enqueueSnackbar, t]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
}

CostModelsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access and manage the state of the Cost Models Context
 */
const useCostModelsContext = () => {
  const costModelsState = useContext(StateContext);
  const costModelsDispatch = useContext(DispatchContext);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // INTERNAL METHODS

  /**
   * Method that fetches the scenario config of a given
   * scenario and returns the cost models available for
   * that scenario.
   *
   * @param {number} scenarioConfigId Id of the scenario config to fetch.
   * @returns {Array} Cost models available for given the scenario.
   */
  async function fetchScenarioConfig(scenarioConfigId) {
    try {
      // Get scenario config (API call).
      const res = await getScenarioConfigs(scenarioConfigId);

      if (res) {
        // Return cost models for the scenario.
        return [...res.cost_models];
      } else {
        return null;
      }
    } catch (error) {
      // Feching scenario config fail.
      costModelsDispatch({
        type: "ERROR_SET",
        payload: { error }
      });
      enqueueSnackbar(t("CostModelConfig.ErrorGettingModels"), {
        variant: "error"
      });
      console.log(error);
      Sentry.captureException(error);
      return null;
    }
  }

  /**
   * Method that fetches the scenario siting config for a given scenario id.
   *
   * @param {number} sitingConfigId Id of the scenario siting config to fetch.
   * @returns {Object|null} Scenario siting config object.
   */
  async function fetchScenarioSitingConfig(sitingConfigId) {
    try {
      if (!sitingConfigId) return null;
      const res = await getSitingScenarioConfigs(sitingConfigId);
      return res || null;
    } catch (error) {
      costModelsDispatch({
        type: "ERROR_SET",
        payload: { error }
      });
      enqueueSnackbar(t("CostModelConfig.ErrorGettingModels"), {
        variant: "error"
      });
      console.error(error);
      Sentry.captureException(error);
      return null;
    }
  }

  /**
   * Method that given an array of cost model names, calls
   * back-end for each one and fetches its cost model parameters.
   * (global, layer and section). It returns an array of objects
   * with the parameters of each given cost model.
   *
   * @param {Array} costModels Array of cost model names.
   * @returns {Array} Cost model parameters.
   */
  async function fetchScenarioCostModelsParameters(costModels) {
    let index;
    let modelsLength = costModels?.length || 0;
    let promises = [];
    let modelNames = [];
    let parameters = [];

    // Iteation of the given cost models.
    for (index = 0; index < modelsLength; index++) {
      const modelName = costModels[index];
      // Back-end call to  retrieve cost data for each model.
      promises.push(getCostModelParameters(modelName));
      // Also store the model name.
      modelNames.push(modelName);
    }

    const results = await Promise.allSettled(promises);
    const failedModels = [];
    let firstError = null;

    // Iterate the result of the fetching and build the parameters object.
    for (let index = 0; index < results.length; index++) {
      const result = results[index];

      if (result.status === "fulfilled") {
        const element = result.value;
        parameters.push({
          name: modelNames[index],
          globalParams: element?.global_p || [],
          layerParams: element?.layer_p || [],
          sectionParams: element?.section_p || []
        });
      } else {
        if (!firstError) firstError = result.reason;
        failedModels.push(modelNames[index]);

        parameters.push({
          name: modelNames[index],
          globalParams: [],
          layerParams: [],
          sectionParams: []
        });
      }
    }

    if (failedModels.length > 0) {
      const error =
        firstError || new Error("Error fetching cost model configs");
      costModelsDispatch({
        type: "ERROR_SET",
        payload: { error }
      });
      enqueueSnackbar(t("CostModelConfig.ErrorGettingModelConfigs"), {
        variant: "error"
      });
      console.error(
        `Error fetching cost model configs for: ${failedModels.join(", ")}`,
        error
      );
      Sentry.captureException(error);
    }

    return parameters;
  }

  /**
   * Method that calls back-end to fetch all cost models for the
   * company with its information (name, label, short-label, docs...).
   *
   * @returns {Array} Cost models information.
   */
  async function fetchCostModelsInfo() {
    try {
      const costModels = await getAvailableCostModels();
      return costModels;
    } catch (error) {
      // Feching data for the available cost models.
      costModelsDispatch({
        type: "ERROR_SET",
        payload: { error }
      });
      enqueueSnackbar(t("CostModelConfig.ErrorGettingModels"), {
        variant: "error"
      });
      console.log(error);
      Sentry.captureException(error);
      return [];
    }
  }

  // PUBLIC METHODS

  /**
   * Method that, given a scenario config id and/or a scenario siting config id,
   * fetches the cost models tied to the scenario, their parameters, and the
   * information of all available cost models in the company.
   *
   * @param {number} scenarioConfigId Scenario config id (routing).
   * @param {number} scenarioSitingConfigId Scenario siting config id (KPIs).
   */
  const fetchCostModelsData = async (
    scenarioConfigId,
    scenarioSitingConfigId = null
  ) => {
    if (!scenarioConfigId && !scenarioSitingConfigId) {
      return;
    }

    costModelsDispatch({
      type: "FETCHING_START"
    });

    // Retrieve cost models linked to the standard scenario config.
    const scenarioCostModels =
      (scenarioConfigId && (await fetchScenarioConfig(scenarioConfigId))) || [];

    // Retrieve KPI (siting) cost models if a siting config exists.
    let sitingCostModels = [];
    if (scenarioSitingConfigId) {
      const sitingConfig = await fetchScenarioSitingConfig(
        scenarioSitingConfigId
      );
      sitingCostModels = sitingConfig?.kpi_enabled_models || [];
    }

    // Merge and deduplicate both lists.
    const mergedCostModels = [
      ...new Set([...scenarioCostModels, ...sitingCostModels])
    ];

    // Fetch parameters for all available models.
    const costsParameters =
      await fetchScenarioCostModelsParameters(mergedCostModels);

    if (costsParameters !== null) {
      const costsInfo = await fetchCostModelsInfo();

      costModelsDispatch({
        type: "COST_MODELS_DATA_SET",
        payload: {
          scenarioCostModels: mergedCostModels,
          scenarioCostModelsParameters: costsParameters,
          costModelsInfo: costsInfo
        }
      });
    }
  };

  /**
   * Method that given a cost model name, returns its information
   * from the current context. That's:
   *   - class_name
   *   - label
   *   - label_short
   *   - doc
   *
   * @param {string} modelName
   * @returns {Object} Object with the cost model info.
   */
  const getCostModelInfo = useCallback(
    modelName => {
      if (
        costModelsState.costModelsInfo &&
        costModelsState.costModelsInfo.length > 0
      ) {
        const info = costModelsState.costModelsInfo.find(
          c => c?.class_name === modelName
        );
        return info;
      } else {
        return {
          class_name: modelName,
          label: modelName,
          label_short: modelName,
          doc: ""
        };
      }
    },
    [costModelsState.costModelsInfo]
  );

  /**
   * Method that returns an array with the names of all the available
   * cost models for the company.
   */
  const getAllAvailableCostModels = useCallback(() => {
    return costModelsState?.costModelsInfo?.map(c => c.class_name);
  }, [costModelsState?.costModelsInfo]);

  return {
    costModelsState,
    fetchCostModelsData,
    fetchScenarioSitingConfig,
    getCostModelInfo,
    getAllAvailableCostModels
  };
};

export { useCostModelsContext };

export default CostModelsProvider;
